import { useEffect, useRef, useState } from 'react';

interface UseChartWideDropZoneOptions {
  enabled?: boolean;
  onDrop: (files: File[]) => void;
}

interface UseChartWideDropZoneResult {
  /** True while a file drag is hovering over the window. */
  isDragging: boolean;
}

/**
 * Attach window-level drag/drop listeners and track drag state.
 *
 * Uses a counter ref to survive the enter/leave churn that fires as the
 * cursor crosses child-element boundaries (a flat `isDragging` boolean
 * would flicker).
 *
 * Listeners are removed on unmount, so this hook is safe to own from a
 * component that only mounts conditionally (e.g. inside a tab).
 */
export function useChartWideDropZone({
  enabled = true,
  onDrop,
}: UseChartWideDropZoneOptions): UseChartWideDropZoneResult {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const onDropRef = useRef(onDrop);

  // Keep the latest onDrop in a ref so we don't reattach listeners on every
  // render (caller may not memoize the handler).
  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  useEffect(() => {
    if (!enabled) return;

    const hasFiles = (e: DragEvent): boolean =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const handleDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragCounter.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };

    const handleDragLeave = () => {
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onDropRef.current(files);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      // Reset in case we unmount mid-drag
      dragCounter.current = 0;
      setIsDragging(false);
    };
  }, [enabled]);

  return { isDragging };
}
